import { Spinner } from '@qr-front/ui/components/Spinner'
import { Tooltip } from '@qr-front/ui/components/Tooltip'
import { useSetAtom } from 'jotai'
import { useTranslations } from 'next-intl'
import { memo, useEffect, useRef, useState } from 'react'

import { TQrDesign } from '@/generator/client'
import { TQrContent } from '@/generator/shared'

import {
	createQrPreviewSvg,
	getCachedQrPreviewSvg,
} from '../../../utils/qrSvgCache'
import { scanQrDialogAtom, scanQrDialogDataAtom } from '../dialogsState'

export type TPreviewCellProps = {
	id: string
	updatedAt: string
	qrDesign: TQrDesign
	content: TQrContent
	uri: string
	disabled: boolean
}

export const PreviewCell = memo(
	({ id, updatedAt, qrDesign, content, uri, disabled }: TPreviewCellProps) => {
		const t = useTranslations()
		const [optimizedPreviewSvgString, setOptimizedPreviewSvgString] =
			useState<string>('')
		const setOpenScanQrModal = useSetAtom(scanQrDialogAtom)
		const setScanQrModalData = useSetAtom(scanQrDialogDataAtom)
		const containerEl = useRef<HTMLButtonElement>(null)

		useEffect(() => {
			if (!qrDesign) {
				setOptimizedPreviewSvgString('')
				return
			}

			let isCancelled = false
			void getCachedQrPreviewSvg({
				id,
				updatedAt,
				qrDesign,
				content,
				uri,
			})
				.then((optimizedSvg) => {
					if (isCancelled) return
					setOptimizedPreviewSvgString(optimizedSvg)
				})
				.catch((error) => {
					console.log('QR preview generation failed', error)
					if (isCancelled) return
					setOptimizedPreviewSvgString('')
				})

			return () => {
				isCancelled = true
			}
		}, [content, id, qrDesign, updatedAt, uri])

		const handleOpenScanQrModal = async () => {
			let previewSvgString = optimizedPreviewSvgString

			if (qrDesign) {
				try {
					const { svg } = await createQrPreviewSvg({
						qrDesign,
						content,
						uri,
					})
					previewSvgString = svg
				} catch (error) {
					console.log('QR preview modal generation failed', error)
				}
			}

			if (!previewSvgString) return

			setScanQrModalData({
				id,
				uri,
				previewSvgString,
				disabled,
				content,
			})
			setOpenScanQrModal(true)
		}

		return (
			<>
				<Tooltip
					anchorRef={containerEl}
					visual="private"
					position="top"
					className="hidden tablet:inline-block"
				>
					{t('dashboard__qr_table____qr_card__preview_tooltip')}
				</Tooltip>
				<button
					ref={containerEl}
					onClick={handleOpenScanQrModal}
					className="relative size-20 shrink-0 rounded-8 p-2 shadow-[0_2px_8px_rgba(0,0,0,0.08)] ring-1 ring-inset ring-border-grey tablet:size-22"
				>
					{optimizedPreviewSvgString ? (
						<div
							dangerouslySetInnerHTML={{ __html: optimizedPreviewSvgString }}
							className="size-full [&>svg]:size-full"
						/>
					) : (
						<Spinner
							size={30}
							color="border-grey"
							className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
						/>
					)}
				</button>
			</>
		)
	},
)

PreviewCell.displayName = 'PreviewCell'
